/**
 * V98-3-3 / 3-4 / 3-5 — client for the Today, coverage / SoD, signature,
 * access-review and nudge endpoints (Track 1 backend, ADR-0046).
 *
 * Every call is workspace-scoped (`/api/w/:slug/*`): the server resolves the
 * workspace and the caller from the session and enforces the actor rules
 * (routed approver, preparer, checker, absentee …). The client never names a
 * workspace or a user to act as.
 */
import type {
  BlockingRow,
  LeadResolveOptions,
  ResubmitState,
  AbsencePreview,
  AbsenceRow,
  AbsenceWithDelegationResult,
  CheckFollowUpFacts,
  CheckFollowUpOptions,
  AccessReviewRowStatus,
  Alternatives,
  CheckerOptions,
  CompensatingDryRun,
  CompensatingReview,
  CoverageAsk,
  DelegationPreview,
  DelegationRow,
  IndependentCheck,
  OwedItem,
  PackageDraft,
  PackagePrerequisite,
  ReviewedArtifact,
  SignatureRequest,
  SignatureRequestEvent,
  SubmitCompensatingResult,
  WhileYouWereOut,
  WillMiss,
} from '@aigovops/core';
import { req, reqText } from './api';

export type {
  AbsencePreview, AbsenceRow, AccessReviewRowStatus, CheckFollowUpFacts, CheckFollowUpOptions, Alternatives, CheckerOptions, CompensatingDryRun, CompensatingReview, CoverageAsk,
  SubmitCompensatingResult,
  DelegationPreview, DelegationRow, IndependentCheck, OwedItem, ReviewedArtifact, SignatureRequestEvent, WhileYouWereOut,
};

const w = (slug: string) => `/api/w/${encodeURIComponent(slug)}`;
const e = encodeURIComponent;
const post = <T>(path: string, body?: unknown) => req<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) });

/** A signature request as the API returns it (routing parsed, frozen content only on the detail read). */
export type SignatureView = Omit<SignatureRequest, 'frozen_content_json' | 'routing_json'> & {
  routing: Array<{ user_id: string | null; via: string; outcome: string; reasons?: string[] }>;
  content?: unknown;
};

// ---- signatures ---------------------------------------------------------------------

export const getSignature = (slug: string, id: string) =>
  req<{ request: SignatureView; events: SignatureRequestEvent[]; /** R3-S2: returned requests only — disable Resubmit while `changed` is false. */ resubmit?: ResubmitState | null;
    /** V98-5-4: a certification package draft's ONE prerequisites list (the approver's card). */ package?: { draft: PackageDraft; prerequisites: PackagePrerequisite[]; blocking: number;
      /** V98-5 review T5-H10: the series' newest version, and who freezes the next one. */ latest?: { request_id: string; version: number; state: string } | null; freezer_user_id?: string | null } | null }>(`${w(slug)}/signatures/${e(id)}`);

export const listMySignatures = (slug: string, state: 'requested' | 'signed') =>
  req<{ requests: SignatureView[] }>(`${w(slug)}/signatures?mine=approver&state=${state}`);

export interface SignBody {
  version: number;
  content_sha256: string;
  /** Recorded by the server; the passkey assertion is not verified yet (TODO WebAuthn, server side). */
  method: 'passkey' | 'code';
  assertion?: string;
  on_behalf_of_user_id?: string;
  delegation_id?: string;
}
export const signSignature = (slug: string, id: string, body: SignBody) =>
  post<{ request: SignatureView }>(`${w(slug)}/signatures/${e(id)}/sign`, body);
export const sendBackSignature = (slug: string, id: string, reason: string) =>
  post<{ request: SignatureView }>(`${w(slug)}/signatures/${e(id)}/send-back`, { reason });
export const undoSignature = (slug: string, id: string) =>
  post<{ request: SignatureView }>(`${w(slug)}/signatures/${e(id)}/undo`, {});
/**
 * S3: the author (preparer) or requester of a RETURNED request freezes v+1 and re-routes it (closes the revise
 * work item). `content` = the revision (default: the returned version's content). 409 unless latest + returned.
 */
/** R3-S2: an artifact with a source is re-frozen from it server-side (send only `change_note`); unchanged → 409 `unchanged`. */
export const resubmitSignature = (slug: string, id: string, body: { content?: unknown; title?: string; facts_version?: string | null; change_note?: string } = {}) =>
  post<{ request: SignatureView }>(`${w(slug)}/signatures/${e(id)}/resubmit`, body);

// ---- lead resolve on a signature request (R3-S3, R3-S4) — owner/admin or the program lead ---------------

export const getLeadActions = (slug: string, requestId: string) =>
  req<{ lead_actions: LeadResolveOptions }>(`${w(slug)}/coverage/items/signature_request/${e(requestId)}/lead-actions`);
/** Allowed only while the holder is away now, no longer assignable, or nobody is routed (409 reassign_not_allowed). */
export const reassignSigner = (slug: string, requestId: string, body: { to_user_id: string; reason: string; until?: string | null }) =>
  post<{ request: SignatureView; preview: unknown }>(`${w(slug)}/coverage/items/signature_request/${e(requestId)}/reassign-signer`, body);
/** Soft due date only, up to the hard date (409 hard_date otherwise). */
export const extendSignatureDue = (slug: string, requestId: string, body: { due_at: string; reason: string }) =>
  post<{ request: SignatureView }>(`${w(slug)}/coverage/items/signature_request/${e(requestId)}/extend`, body);

// ---- work items (S10) ------------------------------------------------------------------

/** The item's current owner (or their live delegate) completes it; asks / checks / revise / derived items are 409. */
export const completeWorkItem = (slug: string, id: string, body: { completion_ref?: string } = {}) =>
  post<unknown>(`${w(slug)}/work-items/${e(id)}/complete`, body);

// ---- assignments (inline assign / reassign — U1) ----------------------------------------------

/** Subject kinds an item's ref can be assigned through (core ASSIGNMENT_SUBJECT_KINDS ∩ what Today links to). */
export const ASSIGNABLE_KINDS: Record<string, string> = { work_item: 'work_item', finding: 'finding', access_review: 'access_review', scn: 'scn' };
export interface AssignmentRow { assignment_id: string; subject_kind: string; subject_id: string; responsibility: string; user_id: string }
/** Owner/admin or the program lead of the subject's program (server-enforced). */
export const assignSubject = (slug: string, body: { subject_kind: string; subject_id: string; responsibility: string; user_id: string; reason?: string }) =>
  post<{ assignment: AssignmentRow }>(`${w(slug)}/assignments`, body);
export interface ReassignPreviewView {
  conflicts: Array<{ subject: { kind: string; id: string }; item_id: string; pair_ids: string[] }>;
  rows_routed_elsewhere: Array<{ review_id: string; item_key: string }>;
  receiver: { user_id: string; absences: Array<{ starts_on: string; ends_on: string; state: string }>; load: { open_work_items: number; signatures_waiting: number; open_checks: number } };
}
/** Owner/admin: who the receiver is (availability, load) and what the SoD pairs refuse — before anything moves (T16). */
export const reassignPreview = (slug: string, body: { subjects: Array<{ kind: string; id: string }>; from_user_id: string; to_user_id: string; responsibility?: string }) =>
  post<{ preview: ReassignPreviewView }>(`${w(slug)}/coverage/reassign/preview`, body);
export const reassignSubjects = (
  slug: string,
  body: { subjects: Array<{ kind: string; id: string }>; from_user_id: string; to_user_id: string; responsibility?: string; reason: string; until?: string | null },
) => post<unknown>(`${w(slug)}/assignments/reassign`, body);

// ---- coverage: absences, delegations, asks ---------------------------------------------

export const listMyAbsences = (slug: string, userId: string) =>
  req<{ absences: Array<Omit<AbsenceRow, 'preview_json'> & { preview?: AbsencePreview }> }>(`${w(slug)}/coverage/absences?user_id=${e(userId)}`);
export const setAbsence = (slug: string, body: { starts_on: string; ends_on: string }) =>
  post<{ absence: AbsenceRow; preview: AbsencePreview }>(`${w(slug)}/coverage/absences`, body);
export const absencePreview = (slug: string, id: string) =>
  req<{ preview: AbsencePreview }>(`${w(slug)}/coverage/absences/${e(id)}/preview`);
export const confirmAbsence = (slug: string, id: string) => post<{ absence: AbsenceRow }>(`${w(slug)}/coverage/absences/${e(id)}/confirm`);
export const declineAbsence = (slug: string, id: string, reason: string) =>
  post<{ absence: AbsenceRow }>(`${w(slug)}/coverage/absences/${e(id)}/decline`, { reason });
export const endAbsence = (slug: string, id: string) => post<{ absence: AbsenceRow }>(`${w(slug)}/coverage/absences/${e(id)}/end`);
/** R3-S6: new dates for your own upcoming absence (cancelled + re-created); Today's `upcoming_absences[].edit`. Cancel = endAbsence. */
export const editAbsence = (slug: string, id: string, body: { starts_on: string; ends_on: string }) =>
  post<{ absence: AbsenceRow; replaced: AbsenceRow; preview: AbsencePreview; rerouted: string[] }>(`${w(slug)}/coverage/absences/${e(id)}/edit`, body);
export const whileYouWereOut = (slug: string, id: string) =>
  req<{ summary: WhileYouWereOut }>(`${w(slug)}/coverage/absences/${e(id)}/while-you-were-out`);

/**
 * U14: your own absence and (optionally) your delegation in ONE request — validated together, written together
 * (a refused delegation saves no absence). `dry_run` returns the previews + uncovered days and writes nothing; a
 * retry of a save that went through returns `replayed` with the same records (never a second absence). The
 * delegation window defaults to the absence days.
 */
export interface AbsenceWithDelegationBody {
  starts_on: string;
  ends_on: string;
  delegation?: { delegate_user_id: string; scope: string[]; starts_at?: string; ends_at?: string } | null;
  dry_run?: boolean;
}
export type AbsenceWithDelegationView = Omit<AbsenceWithDelegationResult, 'absence'> & { absence: (Omit<AbsenceRow, 'preview_json'> & { preview?: AbsencePreview }) | null };
export const saveAbsenceWithDelegation = (slug: string, body: AbsenceWithDelegationBody) =>
  post<AbsenceWithDelegationView>(`${w(slug)}/coverage/absence-with-delegation`, body);

export interface DelegationInput { delegate_user_id?: string; scope: string[]; starts_at: string; ends_at: string }
export const previewDelegation = (slug: string, body: DelegationInput) =>
  post<{ preview: DelegationPreview }>(`${w(slug)}/coverage/delegations/preview`, body);
export const setDelegation = (slug: string, body: Required<DelegationInput>) =>
  post<{ delegation: DelegationRow }>(`${w(slug)}/coverage/delegations`, body);
export const listMyDelegations = (slug: string) => req<{ delegations: DelegationRow[] }>(`${w(slug)}/coverage/delegations`);
export const listCovering = (slug: string) =>
  req<{ covering: Array<{ delegation: DelegationRow; items: OwedItem[] }> }>(`${w(slug)}/coverage/covering`);

export const listMyAsks = (slug: string) => req<{ asks: CoverageAsk[] }>(`${w(slug)}/coverage/asks`);
export const askToDelegate = (slug: string, body: { item_kind: 'signature_request' | 'access_review_row'; item_id: string; message?: string }) =>
  post<{ ask: CoverageAsk }>(`${w(slug)}/coverage/asks`, body);
/**
 * S5: a non-delegable signature (certification package, system description) is never delegated —
 * ask the away approver to sign remotely (`alternatives.options.ask_delegate.ask_kind === 'sign_remote'`).
 */
export const askToSignRemote = (slug: string, body: { item_id: string; message?: string }) =>
  post<{ ask: CoverageAsk }>(`${w(slug)}/coverage/asks`, { kind: 'sign_remote', item_kind: 'signature_request', ...body });
/** The recipient answers a sign_remote ask by signing the frozen version (routed-approver path); "can't" = answerAsk decline. */
export const signRemote = (slug: string, askId: string, body: Omit<SignBody, 'on_behalf_of_user_id' | 'delegation_id'>) =>
  post<{ ask: CoverageAsk; request: SignatureView }>(`${w(slug)}/coverage/asks/${e(askId)}/sign`, body);
export const answerAsk = (
  slug: string, id: string,
  body: { answer: 'decline' | 'delegate' | 'confirm'; reason?: string; delegation?: { delegate_user_id: string; ends_at: string } },
) => post<unknown>(`${w(slug)}/coverage/asks/${e(id)}/answer`, body);

// ---- SoD: alternatives, hold, compensating review, checks ---------------------------------

export type CoverageItemKind = 'signature_request' | 'access_review_row';
export const getAlternatives = (slug: string, kind: CoverageItemKind, id: string) =>
  req<{ alternatives: Alternatives }>(`${w(slug)}/coverage/items/${e(kind)}/${e(id)}/alternatives`);
export const holdItem = (slug: string, kind: CoverageItemKind, id: string) => post<unknown>(`${w(slug)}/coverage/items/${e(kind)}/${e(id)}/hold`);
export const requestCompensating = (
  slug: string,
  body: { item_kind: CoverageItemKind; item_id: string; facts: string; alternatives_tried: string[]; batch_id?: string },
) => post<{ compensating_review: CompensatingReview }>(`${w(slug)}/coverage/compensating-reviews`, body);
/**
 * S7: check eligibility BEFORE anything is written — alternatives, the facts rule, T10 checkers (an empty list is
 * `needs_independent_party`), and a pending review to resume (`pending_comp_review_id`). Preparer or owner/admin.
 */
export const compensatingDryRun = (
  slug: string,
  body: { item_kind: CoverageItemKind; item_id: string; facts?: string; alternatives_tried?: string[] },
) => post<{ dry_run: CompensatingDryRun }>(`${w(slug)}/coverage/compensating-reviews/dry-run`, body);
/** S7: ONE submit — facts + alternatives + checker; resumes a pending review; nobody eligible → no record. */
export const submitCompensating = (
  slug: string,
  body: {
    item_kind: CoverageItemKind; item_id: string; facts: string; alternatives_tried: string[]; batch_id?: string;
    checker_user_id?: string; due_at?: string;
  },
) => post<SubmitCompensatingResult>(`${w(slug)}/coverage/compensating-reviews/submit`, body);
export const getCompensating = (slug: string, id: string) =>
  req<{ compensating_review: CompensatingReview; checks: IndependentCheck[] }>(`${w(slug)}/coverage/compensating-reviews/${e(id)}`);
export const eligibleCheckers = (slug: string, compId: string) => req<CheckerOptions>(`${w(slug)}/coverage/compensating-reviews/${e(compId)}/checkers`);
export const selfApprove = (slug: string, compId: string, body: { checker_user_id: string; due_at: string }) =>
  post<{ status: string }>(`${w(slug)}/coverage/compensating-reviews/${e(compId)}/self-approve`, body);
export const concludeCheck = (
  slug: string, checkId: string, body: { conclusion: 'agree' | 'exception'; note: string; reviewed_artifacts: ReviewedArtifact[] },
) => post<unknown>(`${w(slug)}/coverage/checks/${e(checkId)}/conclude`, body);
export const acknowledgeEscalation = (slug: string, checkId: string) => post<unknown>(`${w(slug)}/coverage/checks/${e(checkId)}/acknowledge`);
/**
 * U1: an overdue check's follow-up — the escalation owner (or an owner/admin outside the item). The checker by
 * name, the 30-day bound from the original self-approval, extensions used, and who could take it over (with the
 * reason each person can't). 403 for anyone else.
 */
export const checkFollowUp = (slug: string, checkId: string) =>
  req<{ follow_up: CheckFollowUpOptions }>(`${w(slug)}/coverage/checks/${e(checkId)}/follow-up`);
/** A new checker (re-passes the independence rules), with a reason; the old check is superseded, never edited. */
export const reassignCheck = (slug: string, checkId: string, body: { checker_user_id: string; reason: string; due_at?: string }) =>
  post<{ check: IndependentCheck; superseded: IndependentCheck }>(`${w(slug)}/coverage/checks/${e(checkId)}/reassign`, body);
/** A later check-by date, ≤ 30 days from the original self-approval, with a reason (one extension unless you're the escalation owner). */
export const extendCheck = (slug: string, checkId: string, body: { due_at: string; reason: string }) =>
  post<{ check: IndependentCheck; superseded: IndependentCheck }>(`${w(slug)}/coverage/checks/${e(checkId)}/extend`, body);
/**
 * S6: seating an independent party invites them in the same call. `state` is 'active' when an existing member
 * fills the seat, else 'invited'. `invite.state` is 'sent' only when the email went out; 'invite_pending' means
 * share `invite.accept_url` (Copy link) — never show "sent" for it.
 */
export interface IndependentPartySeatResult {
  party_id: string;
  state: 'active' | 'invited';
  user_id: string | null;
  invite: null | {
    state: 'sent' | 'invite_pending';
    invitation_id: string;
    invited_email: string;
    expires_at: string;
    accept_url: string;
    email_delivery: { status: 'sent' | 'not_configured' | 'error'; message_id?: string; detail?: string };
  };
}
/** R3-S5: `for` lets the review's program lead seat a member; an unknown email from a lead comes back 202 { status: 'asked' }. */
export const addIndependentParty = (slug: string, body: { kind: 'advisor_seat' | 'board_contact'; contact_name: string; contact_email: string; user_id?: string | null; for?: { kind: 'access_review'; id: string } }) =>
  post<IndependentPartySeatResult>(`${w(slug)}/coverage/independent-parties`, body);
/** Resend: a fresh invitation (new link) for a seat nobody fills yet. */
export const resendIndependentPartyInvite = (slug: string, partyId: string) =>
  post<IndependentPartySeatResult>(`${w(slug)}/coverage/independent-parties/${e(partyId)}/resend`);
/** A seat counts as an independent party only when `state === 'active'` (an active member fills it). */
export interface IndependentPartyView {
  party_id: string;
  kind: string;
  user_id: string | null;
  contact_name: string;
  contact_email: string | null;
  state: 'active' | 'invited' | 'invite_expired' | 'contact_only';
  invitation: { invitation_id: string; expires_at: string; accepted_at: string | null; revoked_at: string | null } | null;
}
export const coverageConfig = (slug: string) =>
  req<{ independent_parties: IndependentPartyView[] }>(`${w(slug)}/coverage/config`);

// ---- access reviews ----------------------------------------------------------------------

/** R3-S5: what THIS caller can do about rows nobody eligible can take (null when there are none). */
export interface PartyActions {
  add_party: { available: boolean; can_invite: boolean; method: 'POST'; path: string; for: { kind: 'access_review'; id: string }; why: string };
  ask_lead: { available: boolean; method: 'POST'; path: string; for: { kind: 'access_review'; id: string }; to_user_id: string | null; why: string };
}
export const accessReviewRows = (slug: string, id: string) =>
  req<{ rows: AccessReviewRowStatus[]; approved: number; total: number; all_approved: boolean; blocking?: BlockingRow[]; actions?: PartyActions | null }>(`${w(slug)}/access-reviews/${e(id)}/rows`);
/** R3-S5: the reviewer (or a lead who can't invite) asks the program lead / an admin to add an independent party. */
export const askForIndependentParty = (slug: string, reviewId: string, message?: string) =>
  post<{ status: 'asked'; work_item_id: string; to_user_id: string; reused: boolean }>(`${w(slug)}/coverage/independent-parties/ask`, { for: { kind: 'access_review', id: reviewId }, ...(message ? { message } : {}) });
export const decideAccessRow = (slug: string, id: string, body: { item_key: string; decision: 'keep' | 'revoke'; note?: string }) =>
  post<unknown>(`${w(slug)}/access-reviews/${e(id)}/decisions`, body);
export const approveAccessRow = (slug: string, id: string, itemKey: string) =>
  post<unknown>(`${w(slug)}/access-reviews/${e(id)}/rows/${e(itemKey)}/approve`);
export const completeAccessReview = (slug: string, id: string) => post<unknown>(`${w(slug)}/access-reviews/${e(id)}/complete`);

// ---- nudges + blockers (lead / admin; server-scoped) ------------------------------------------

export const nudge = (slug: string, body: { item_key: string; holder_user_id?: string; note?: string }) =>
  post<{ nudge: { to_user_id: string; for_user_id: string | null } }>(`${w(slug)}/nudges`, body);

/** One open item visible to the caller (GET /blockers — the caller-scoped blockers list; the CSV export has the same rows). */
export interface BlockerRow {
  item_key: string;
  /** R3-S4: the server says the viewer may assign / reassign it (owner/admin or the item's program lead). */
  can_assign?: boolean;
  /** S11: the record to open (signature request, work item, review …) — present on the JSON list, absent from the CSV. */
  ref?: { kind: string; id: string };
  /** S10: for work items, the kind of work (determination, scn_classification, coverage_request, evidence, …). */
  subtype?: string | null;
  title: string;
  next_action: string;
  hat: string;
  holders: string[];
  holder_ids: string[];
  due_at: string | null;
  hard_date: string | null;
  overdue: boolean;
  blocked: boolean;
  programs: string[];
  last_nudged_at: string | null;
}

/** RFC 4180 parse; undoes the export's CSV-injection guard (a leading `'` before = + - @ tab CR). */
export function parseBlockersCsv(text: string): BlockerRow[] {
  const records: string[][] = [];
  let row: string[] = [], cell = '', quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell); records.push(row); row = []; cell = '';
    } else cell += ch;
  }
  if (cell || row.length) { row.push(cell); records.push(row); }
  const [head, ...body] = records.filter((r) => r.length > 1 || r[0]);
  if (!head) return [];
  const idx = (k: string) => head.indexOf(k);
  const unguard = (s: string) => (/^'[=+\-@\t\r]/.test(s) ? s.slice(1) : s);
  const list = (s: string) => (s ? s.split('; ').map(unguard) : []);
  const val = (r: string[], k: string) => unguard(r[idx(k)] ?? '');
  return body.map((r) => ({
    item_key: val(r, 'item_key'),
    title: val(r, 'title'),
    next_action: val(r, 'next_action'),
    hat: val(r, 'hat'),
    holders: list(r[idx('holders')] ?? ''),
    holder_ids: list(r[idx('holder_ids')] ?? ''),
    due_at: val(r, 'due_at') || null,
    hard_date: val(r, 'hard_date') || null,
    overdue: val(r, 'overdue') === 'yes',
    blocked: val(r, 'blocked') === 'yes',
    programs: list(r[idx('programs')] ?? ''),
    last_nudged_at: val(r, 'last_nudged_at') || null,
  }));
}

/** S11: GET /blockers (JSON) — the server's own rows, each with `ref`; no CSV parsing. */
export interface BlockerListResponse {
  scope: 'org' | 'programs' | 'own';
  generated_at: string;
  rows: Array<{
    item_key: string; ref: { kind: string; id: string }; subtype: string | null; title: string; action: string; hat: string;
    holder_user_ids: string[]; holder_names: string[]; due_at: string | null; hard_date: string | null; overdue: boolean; blocked: boolean;
    program_ids: string[]; last_nudged_at: string | null; can_assign?: boolean;
  }>;
  next_hard_date: string | null;
  will_miss: WillMiss | null;
}
export const getBlockers = (slug: string) => req<BlockerListResponse>(`${w(slug)}/blockers`);
export const listBlockers = async (slug: string): Promise<BlockerRow[]> =>
  (await getBlockers(slug)).rows.map((r) => ({
    item_key: r.item_key, ref: r.ref, subtype: r.subtype, title: r.title, next_action: r.action, hat: r.hat, holders: r.holder_names,
    holder_ids: r.holder_user_ids, due_at: r.due_at, hard_date: r.hard_date, overdue: r.overdue, blocked: r.blocked, programs: r.program_ids,
    last_nudged_at: r.last_nudged_at, can_assign: r.can_assign,
  }));

export const blockersSummary = (slug: string): Promise<string> => reqText(`${w(slug)}/blockers/summary`);
